(() => {
 const brandIconSrc = new URL("ws-brand-icon-light.svg?v=smooth-edge-20261016", document.currentScript.src).href;
 const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
 let outlineId = 0;

 function animateOutline(details, summary, nav) {
  let animation = null;
  let expanded = details.open;
  let endHeight = 0;
  const heightFor = open => {
    const styles = getComputedStyle(details);
    return summary.getBoundingClientRect().height + (open ? nav.getBoundingClientRect().height : 0)
      + parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
  };
  const finish = open => {
    details.open = open;
    details.style.removeProperty('height');
    details.removeAttribute('data-animating');
    animation = null;
  };
  const setExpanded = open => {
    const startHeight = details.getBoundingClientRect().height;
    animation?.cancel();
    animation = null;
    expanded = open;
    summary.setAttribute('aria-expanded', String(open));
    details.dataset.outlineState = open ? 'open' : 'closed';
    // Keep closing content out of the tab order while its height settles.
    if (!open && nav.contains(document.activeElement)) summary.focus({preventScroll: true});
    nav.inert = !open;
    if (reducedMotion.matches || !details.animate) {
      finish(open);
      return;
    }
    details.open = true;
    endHeight = heightFor(open);
    if (Math.abs(startHeight - endHeight) < 1) {
      finish(open);
      return;
    }
    details.dataset.animating = 'true';
    details.style.height = `${startHeight}px`;
    const current = details.animate(
      [{height: `${startHeight}px`}, {height: `${endHeight}px`}],
      {duration: 380, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards'}
    );
    animation = current;
    current.onfinish = () => {
      if (animation !== current) return;
      // Commit native disclosure state before cancelling the filled animation.
      finish(open);
      current.cancel();
    };
  };
  summary.setAttribute('aria-expanded', String(expanded));
  details.dataset.outlineState = expanded ? 'open' : 'closed';
  nav.inert = !expanded;
  summary.addEventListener('click', event => {
    event.preventDefault();
    setExpanded(!expanded);
  });
  details.addEventListener('toggle', () => {
    // Native find-in-page and assistive technology can also open <details>.
    if (animation) return;
    expanded = details.open;
    summary.setAttribute('aria-expanded', String(expanded));
    details.dataset.outlineState = expanded ? 'open' : 'closed';
    nav.inert = !expanded;
  });
  if ('ResizeObserver' in window) {
    const resize = new ResizeObserver(() => {
      if (!details.isConnected) { resize.disconnect(); return; }
      if (animation && Math.abs(heightFor(expanded) - endHeight) > 1) setExpanded(expanded);
    });
    resize.observe(summary);
    resize.observe(nav);
  }
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches && animation) setExpanded(expanded);
  });
 }

 function prepareSectionLinks(journal) {
  if (!journal || journal.dataset.readingLinksReady) return;
  journal.dataset.readingLinksReady = 'true';
  journal.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const destination = new URL(link.href, location.href);
    if (!destination.hash || destination.origin !== location.origin || destination.pathname !== location.pathname || destination.search !== location.search) return;
    let id;
    try { id = decodeURIComponent(destination.hash.slice(1)); } catch { return; }
    const target = document.getElementById(id);
    if (!target || !journal.contains(target)) return;
    event.preventDefault();
    if (location.hash !== destination.hash) history.pushState(history.state, '', destination.href);
    const temporaryFocus = !target.hasAttribute('tabindex') && !target.matches('a[href],button,input,select,textarea');
    if (temporaryFocus) {
      target.tabIndex = -1;
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), {once: true});
    }
    target.focus({preventScroll: true});
    target.scrollIntoView({behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'start'});
  });
 }

 function prepareEditorial(journal, article) {
  if (!journal || journal.dataset.editorialReady) return;
  journal.dataset.editorialReady = 'true';
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const metadata = journal.querySelector('.ws-editorial-metadata');
  const shares = journal.querySelector('[data-article-share]');
  const dated = [...article.querySelectorAll('p')].filter(p => !p.closest('li')).map(p => p.textContent.trim()).map(text => text.match(/\b(Updated|Editorially reviewed|Reviewed)\s*:?\s*(\d{1,2}\s+[A-Za-z]+\s+20\d{2})\b/i)).find(Boolean);
  if (dated && metadata && !metadata.querySelector('.ws-editorial-date')) {
    const date = make('div', 'ws-editorial-date');
    date.append(make('span', 'ws-editorial-label', dated[1]), make('span', '', dated[2]));
    metadata.insertBefore(date, shares);
  }
  if (shares) {
    const clean = new URL(location.href);
    ['q','topic','shown','library_scroll'].forEach(key => clean.searchParams.delete(key));
    clean.hash = '';
    // A share contains the article address, never the visitor's library search state.
    const title = journal.querySelector('h1')?.textContent || document.title;
    const status = make('span', 'ws-share-status');
    status.setAttribute('role', 'status');
    const copy = make('button', 'ws-share-button', 'Copy link');
    copy.type = 'button';
    copy.setAttribute('aria-label', 'Copy article link');
    copy.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(clean.href);
        status.textContent = 'Link copied';
      } catch (_) {
        status.textContent = 'Copy this article link:';
        let fallback = shares.querySelector('input');
        if (!fallback) {
          fallback = make('input', 'ws-share-url');
          fallback.readOnly = true;
          fallback.setAttribute('aria-label', 'Article link');
          shares.append(fallback);
        }
        fallback.value = clean.href;
        fallback.focus();
        fallback.select();
      }
    });
    shares.append(copy);
    if (navigator.share) {
      const share = make('button', 'ws-share-button', 'Share');
      share.type = 'button';
      share.addEventListener('click', async () => {
        try { await navigator.share({title, url: clean.href}); }
        catch (error) { if (error.name !== 'AbortError') status.textContent = 'Use Copy link to share this article.'; }
      });
      shares.append(share);
    }
    shares.append(status);
  }
  const cta = make('section', 'ws-article-next');
  cta.setAttribute('aria-label', 'Find your WeightStart plan');
  const copy = make('div', 'ws-article-next-copy');
  copy.append(make('p', 'ws-editorial-label', 'Your next step'), make('h2', '', 'Make this part of your week.'), make('p', '', 'Answer a few questions about your goals, routine and preferences. Find the training, nutrition and support that fit you.'));
  const link = make('a', 'ws-article-next-button', 'Is WeightStart right for me?');
  const destination = new URL('/', location.origin);
  const dataset = document.getElementById('ws-health-draft-data');
  if (dataset) {
    try { destination.searchParams.set('preview_theme_id', JSON.parse(dataset.textContent).theme_id); } catch (_) {}
  }
  destination.hash = 'ws-intake';
  link.href = destination.pathname + destination.search + destination.hash;
  copy.append(link);
  const art = make('div', 'ws-article-next-art');
  art.setAttribute('aria-hidden', 'true');
  art.innerHTML = '<div class="ws-next-paper"><span class="ws-next-brand ws-brand-lockup"><img class="ws-brand-lockup__icon" src="' + brandIconSrc + '" width="24" height="24" alt="">WeightStart</span><div class="ws-next-row"><span>01</span>Training</div><div class="ws-next-row"><span>02</span>Nutrition</div><div class="ws-next-row"><span>03</span>Your support</div></div>';
  cta.append(copy, art);
  journal.append(cta);
 }

 function prepareReader() {
  'use strict';
  const article = document.querySelector('.ws-research-copy');
  const tools = document.querySelector('[data-reader-tools]');
  if (!article || !tools || article.dataset.readingReady) return;
  article.dataset.readingReady = 'true';
  prepareEditorial(article.closest('.ws-research-journal'), article);
  const headings = [...article.querySelectorAll('h2')];
  if (headings.length >= 4) {
    const details = document.createElement('details');
    details.className = 'ws-reading-outline';
    const summary = document.createElement('summary');
    summary.textContent = 'In this article';
    const icon = document.createElement('span');
    icon.className = 'ws-outline-icon';
    icon.setAttribute('aria-hidden', 'true');
    summary.append(icon);
    const nav = document.createElement('nav');
    nav.id = `ws-article-outline-${++outlineId}`;
    nav.setAttribute('aria-label', 'Article sections');
    summary.setAttribute('aria-controls', nav.id);
    const list = document.createElement('ol');
    headings.forEach((heading, index) => {
      if (!heading.id) {
        let id = `reading-section-${index + 1}`;
        while (document.getElementById(id)) id += '-section';
        heading.id = id;
      }
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      item.append(link);
      list.append(item);
    });
    nav.append(list);
    details.append(summary, nav);
    tools.append(details);
    tools.hidden = false;
    animateOutline(details, summary, nav);
  }
  prepareSectionLinks(article.closest('.ws-research-journal'));
  article.querySelectorAll('table').forEach((table, index) => {
    if (table.parentElement.classList.contains('ws-reading-table')) return;
    const wrapper = document.createElement('div');
    wrapper.className = 'ws-reading-table';
    const wide = Math.max(...[...table.rows].map(row => [...row.cells].reduce((sum,cell) => sum + cell.colSpan, 0))) > 2;
    if (wide) wrapper.classList.add('ws-reading-table-wide');
    const hint = document.createElement('p');
    hint.className = 'ws-table-hint';
    hint.textContent = 'Scroll sideways to see the full table.';
    hint.hidden = true;
    wrapper.setAttribute('role', 'region');
    wrapper.setAttribute('aria-label', table.caption?.textContent || `Comparison table ${index + 1}`);
    table.before(hint, wrapper);
    wrapper.append(table);
    const update = () => {
      const overflow = wrapper.scrollWidth > wrapper.clientWidth + 1;
      hint.hidden = !overflow;
      if (overflow) wrapper.tabIndex = 0;
      else wrapper.removeAttribute('tabindex');
    };
    update();
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(wrapper);
  });
 }
 prepareReader();
 document.addEventListener('ws:article-ready', prepareReader);
})();
